import { Telemetry } from '../hooks/useTelemetry';

/**
 * Something outside React (an extension or classroom-management tool —
 * unconfirmed) sometimes detaches `<body>` mid-lesson, so `document.body`
 * returns `null`. Every `document.body.x(...)` then throws, and the first one
 * inside a React commit takes the student's screen down. In production that
 * is the `<NULL_ACCESS:addEventListener>` / `removeEventListener` /
 * `appendChild` and React #200 buckets (22 crashes in the week to
 * 2026-10-02, roughly half Windows, half ChromeOS). Some of those calls are
 * ours (#176 fixes those), but some are in third-party code we can't edit —
 * e.g. Excalidraw's tooltip does `document.body.appendChild` in an effect
 * cleanup.
 *
 * While the real body is missing, this guard makes `document.body` return a
 * detached fallback `<body>` instead of `null`. Calls on it succeed and do
 * nothing visible (listeners never fire, appended nodes aren't rendered),
 * which is the right outcome for code that is tearing down or attaching a
 * global listener. As soon as a real body is back, `document.body` returns
 * it again. A healthy page never touches the fallback.
 */
let installed = false;
let reported = false;

export const installDocumentBodyGuard = () => {
  if (installed || typeof document === 'undefined') return;
  const descriptor = Object.getOwnPropertyDescriptor(
    Document.prototype,
    'body',
  );
  const nativeGet = descriptor?.get;
  if (!nativeGet || !descriptor.configurable) return;
  installed = true;

  let fallback: HTMLElement | null = null;

  Object.defineProperty(document, 'body', {
    configurable: true,
    enumerable: descriptor.enumerable,
    get(this: Document) {
      const body = nativeGet.call(this) as HTMLElement | null;
      if (body) return body;
      if (!reported) {
        reported = true;
        try {
          Telemetry()?.addPageAction('DocumentBodyMissing', {
            childCount: this.documentElement?.childElementCount ?? -1,
          });
        } catch {
          // Telemetry must never throw from inside a property getter.
        }
      }
      fallback ??= this.createElement('body');
      return fallback;
    },
    set: descriptor.set,
  });
};
